import { Data, Effect } from 'effect';
import { okraApi } from '../okraApi';

export class AgentsEndpointError extends Data.TaggedError('AgentsEndpointError')<{
  readonly operation: 'update-url';
}> {}

const agentsEndpointCauses = new WeakMap<AgentsEndpointError, unknown>();

function agentsEndpointFailure(cause: unknown): AgentsEndpointError {
  const error = new AgentsEndpointError({ operation: 'update-url' });
  agentsEndpointCauses.set(error, cause);
  return error;
}

export function agentsEndpointErrorCause(error: AgentsEndpointError): unknown {
  return agentsEndpointCauses.get(error);
}

export const agentEmailPrefixUrlEffect = Effect.fn('web.api.agents.emailPrefixUrl')(function* (
  agentId: string,
) {
  return yield* Effect.try({
    try: () => `/v1/agents/${agentId}`,
    catch: agentsEndpointFailure,
  });
});

function agentEmailPrefixUrl(agentId: string): string {
  return Effect.runSync(agentEmailPrefixUrlEffect(agentId).pipe(Effect.orDie));
}

export const agentsEndpoints = okraApi.injectEndpoints({
  endpoints: (build) => ({
    listAgents: build.query<{ data: Array<{ id: string; table_id: string | null; email: string | null }> }, void>({
      query: () => '/v1/agents',
      providesTags: ['Agents'],
    }),
    createAgent: build.mutation<
      { id: string; name: string; table_id: string | null; email: string | null },
      { name: string; table_id: string; system: string }
    >({
      query: (body) => ({ url: '/v1/agents', method: 'POST', body }),
      invalidatesTags: ['Agents'],
    }),
    updateAgentEmailPrefix: build.mutation<
      { id: string; email: string | null },
      { agentId: string; prefix: string }
    >({
      query: ({ agentId, prefix }) => ({
        url: agentEmailPrefixUrl(agentId),
        method: 'PATCH',
        body: { email_prefix: prefix },
      }),
      invalidatesTags: ['Agents'],
    }),
  }),
});

export const {
  useListAgentsQuery,
  useCreateAgentMutation,
  useUpdateAgentEmailPrefixMutation,
} = agentsEndpoints;
